import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  TrendingUp,
  MapPin,
  Award,
  Hash,
  ArrowLeft,
} from 'lucide-react';
import { useSocialFeed } from '../../context/SocialFeedContext';
import type { PetForSocial } from './socialTypes';
import type { SocialPost } from './socialTypes';
import { SocialPostImage } from './SocialPostImage';
import { PostDetailModal } from './PostDetailModal';
import { TrendingPetFeedView } from './TrendingPetFeedView';
import { toast } from 'sonner';
import * as socialApi from '@/api/social';

interface ExplorePageProps {
  userType: 'owner' | 'professional';
  userId: string;
  category?: 'trending' | 'nearby' | 'professionals' | 'challenges';
  onFilterClick?: (
    category: 'trending' | 'nearby' | 'professionals' | 'challenges'
  ) => void;
  onBackToFeed?: () => void;
  onJoinChallenge?: (tag: string) => void;
  /** User's pets and pets from feed – used for search (e.g. "Alfie", #Alfie) */
  pets?: PetForSocial[];
  onViewPetProfile?: (petId: string, name?: string, avatar?: string, trendingScore?: number) => void;
}

type ExploreCategory = 'trending' | 'nearby' | 'professionals' | 'challenges';

/** Extract hashtag words from text (e.g. "#Alfie" -> "alfie") */
function extractHashtags(text: string): string[] {
  const matches = text.match(/#(\w+)/g);
  if (!matches) return [];
  return [...new Set(matches.map((m) => m.slice(1).toLowerCase()))];
}

export function ExplorePage({
  userType: _userType,
  userId,
  category = 'trending',
  onFilterClick,
  onBackToFeed,
  onJoinChallenge,
  pets = [],
  onViewPetProfile,
}: ExplorePageProps) {
  const { t } = useTranslation();
  const { posts, likePost, unlikePost, addComment } = useSocialFeed();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<
    'trending' | 'nearby' | 'professionals' | 'challenges'
  >(category);
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);
  const [followedPetIds, setFollowedPetIds] = useState<Set<string>>(new Set());
  const [followLoadingPetId, setFollowLoadingPetId] = useState<string | null>(
    null
  );
  const [selectedPetFeed, setSelectedPetFeed] = useState<{
    id: string;
    name: string;
    avatar: string;
    image: string;
    ownerName?: string;
    ownerAvatar?: string;
  } | null>(null);

  // Update active category when prop changes; clear pet feed sub-view
  useEffect(() => {
    setActiveCategory(category);
    setSelectedPetFeed(null);
  }, [category]);

  useEffect(() => {
    let cancelled = false;
    socialApi
      .getFollowing({ limit: 500 })
      .then((res) => {
        if (cancelled) return;
        const list = res?.following ?? [];
        const petIds = new Set(
          list
            .filter((i) => i?.type === 'PET' && i?.id)
            .map((i) => i.id as string)
        );
        setFollowedPetIds(petIds);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedPost) return;
    const updated = posts.find((p) => p.id === selectedPost.id);
    if (updated && updated !== selectedPost) {
      setSelectedPost(updated);
    }
  }, [posts, selectedPost]);

  const normalizedQuery = searchQuery.trim().toLowerCase().replace(/^#/, '');
  const hasSearch = normalizedQuery.length > 0;

  const searchResults = useMemo(() => {
    if (!hasSearch)
      return {
        pets: [],
        hashtags: [] as { tag: string; count: number }[],
        posts: [] as SocialPost[],
      };
    const q = normalizedQuery;
    const matchingPetsFromUser = pets.filter((p) =>
      p.name.toLowerCase().includes(q)
    );
    const tagToCount: Record<string, number> = {};
    const matchingPosts: SocialPost[] = [];
    for (const post of posts) {
      const captionLower = (post.caption ?? '').toLowerCase();
      const captionTags = extractHashtags(post.caption ?? '');
      const postTags = (post.tags ?? []).map((t) => t.toLowerCase());
      const allTags = [...new Set([...captionTags, ...postTags])];
      const captionMatches = captionLower.includes(q);
      const tagMatches = allTags.some((t) => t.includes(q) || q.includes(t));
      if (captionMatches || tagMatches) {
        matchingPosts.push(post);
      }
      for (const tag of allTags) {
        if (tag.includes(q) || q.includes(tag)) {
          const key = `#${tag}`;
          tagToCount[key] = (tagToCount[key] ?? 0) + 1;
        }
      }
    }
    const matchingTaggedPets = matchingPosts
      .flatMap((p) => p.taggedPets ?? [])
      .filter((tp) => tp.name.toLowerCase().includes(q));
    const seenTagged = new Set<string>();
    const uniqueTaggedPets = matchingTaggedPets.filter((tp) => {
      if (seenTagged.has(tp.id)) return false;
      seenTagged.add(tp.id);
      return true;
    });
    const matchingPets = [...matchingPetsFromUser];
    for (const tp of uniqueTaggedPets) {
      if (!matchingPets.some((p) => p.id === tp.id)) {
        matchingPets.push({
          id: tp.id,
          name: tp.name,
          avatar: tp.avatar,
          species: tp.species,
        });
      }
    }
    const hashtags = Object.entries(tagToCount).map(([tag, count]) => ({
      tag,
      count,
    }));
    return { pets: matchingPets, hashtags, posts: matchingPosts };
  }, [hasSearch, normalizedQuery, pets, posts]);

  // Multi-signal ranking algorithm with priority boosts
  const scorePost = useCallback((post: SocialPost, now: number): number => {
    const comments = post.comments ?? [];
    const likes = post.likes ?? 0;
    const createdAtMs =
      typeof post.createdAt === 'number' ? post.createdAt : null;
    const ageHours =
      createdAtMs != null ? Math.max(0, (now - createdAtMs) / 3_600_000) : 72;

    // Engagement quality: comments weighted higher than likes
    const qualityScore = likes * 3 + comments.length * 5;

    // Engagement velocity: newer posts get a multiplier (Primary signal 1)
    const velocityMultiplier = ageHours < 24 ? 2.0 : ageHours < 48 ? 1.5 : 1.0;

    // Time decay bonus for posts within 48 hours (Primary signal 3)
    const recencyBonus = Math.max(0, 48 - ageHours);

    // Unique user engagement proxy — more comments ≈ more unique users (Primary signal 4)
    const uniqueBonus = Math.min(comments.length * 2, 20);

    // Profile credibility bonus (Primary signal 6)
    const credibilityBonus = post.verified ? 15 : 0;

    // Community discussion / interaction bonus (Primary signal 7)
    const discussionBonus = comments.length > 3 ? 10 : 0;

    // Media quality bonus — posts with images rank higher (Secondary signal 1)
    const mediaBonus = (post.images?.length ?? 0) > 0 ? 10 : 0;

    let score =
      qualityScore * velocityMultiplier +
      recencyBonus +
      uniqueBonus +
      credibilityBonus +
      discussionBonus +
      mediaBonus;

    // Priority boost multiplier for Javvad Azam and pet Alfie (Primary signals 8 & 9)
    const owner = (post.ownerName ?? '').toLowerCase();
    const caption = (post.caption ?? '').toLowerCase();
    const tags = (post.tags ?? []).map((tg) => tg.toLowerCase());
    const taggedPetNames = (post.taggedPets ?? []).map((p) =>
      p.name.toLowerCase()
    );
    const isJavvad = owner.includes('javvad azam');
    const isAlfie =
      caption.includes('alfie') ||
      tags.some((tg) => tg.includes('alfie')) ||
      taggedPetNames.some((n) => n.includes('alfie'));

    if (isJavvad && isAlfie)
      score *= 1.9; // +90% boost when both apply
    else if (isJavvad || isAlfie) score *= 1.75; // +75% boost

    return isNaN(score) ? 0 : score;
  }, []);

  const trendingPosts = useMemo(() => {
    const now = Date.now();
    const scored = [...posts]
      .map((post) => ({ post, score: scorePost(post, now) }))
      .sort((a, b) => b.score - a.score);

    // Anti-spam: cap max 3 posts per owner in top results; lower-priority overflow appended after
    const ownerCount = new Map<string, number>();
    const top: SocialPost[] = [];
    const overflow: SocialPost[] = [];
    for (const { post } of scored) {
      const key = post.ownerId || post.ownerName || 'unknown';
      const count = ownerCount.get(key) ?? 0;
      if (count < 3) {
        top.push(post);
        ownerCount.set(key, count + 1);
      } else {
        overflow.push(post);
      }
    }
    return [...top, ...overflow];
  }, [posts, scorePost]);

  const trendingPets = useMemo(() => {
    const now = Date.now();
    type PetStat = {
      id: string;
      name: string;
      avatar: string;
      score: number;
      image: string;
      topPost: SocialPost | null;
      isTagged: boolean;
    };
    const stats = new Map<string, PetStat>();

    for (const post of trendingPosts) {
      const postScore = scorePost(post, now);
      const tagged = post.taggedPets ?? [];

      if (tagged.length > 0) {
        // Primary: accumulate score for explicitly tagged pets
        for (const pet of tagged) {
          const key = `pet:${pet.id}`;
          const prev = stats.get(key);
          if (!prev) {
            stats.set(key, {
              id: pet.id,
              name: pet.name,
              avatar: pet.avatar ?? '🐾',
              score: postScore,
              image: post.images?.[0] ?? '',
              topPost: post,
              isTagged: true,
            });
          } else {
            prev.score += postScore;
            if (!prev.image && post.images?.[0]) prev.image = post.images[0];
            if (!prev.topPost) prev.topPost = post;
          }
        }
      } else if (post.ownerId === userId && pets.length > 0) {
        // Fallback: post belongs to current user with no tagged pets — use their actual pets
        const fallbackScore = postScore * 0.7;
        for (const pet of pets) {
          const key = `pet:${pet.id}`;
          const prev = stats.get(key);
          if (!prev) {
            stats.set(key, {
              id: pet.id,
              name: pet.name,
              avatar: pet.avatar ?? '🐾',
              score: fallbackScore,
              image: post.images?.[0] ?? '',
              topPost: post,
              isTagged: false,
            });
          } else {
            prev.score += fallbackScore;
            if (!prev.image && post.images?.[0]) prev.image = post.images[0];
            if (!prev.topPost) prev.topPost = post;
          }
        }
      }
    }

    // Tagged pets rank before owner-fallback entries; within each group sort by score
    const all = Array.from(stats.values()).sort((a, b) => {
      if (a.isTagged !== b.isTagged) return a.isTagged ? -1 : 1;
      return b.score - a.score;
    });
    return all.slice(0, 8).map((pet) => ({
      ...pet,
      followers:
        isNaN(pet.score) || pet.score <= 0
          ? '0'
          : String(Math.round(pet.score)),
    }));
  }, [trendingPosts, scorePost, userId, pets]);

  const exploreGrid = useMemo(
    () =>
      trendingPosts.slice(0, 18).map((post) => ({
        id: post.id,
        image: post.images?.[0] ?? '',
        likes: post.likes ?? 0,
      })),
    [trendingPosts]
  );

  /** Ranked leaderboard of trending posts with scores for display */
  const trendingLeaderboard = useMemo(() => {
    const now = Date.now();
    return [...posts]
      .map((post) => {
        const eng = (post.likes ?? 0) * 3 + (post.comments ?? []).length * 5;
        const final = scorePost(post, now);
        return { post, eng, final };
      })
      .filter((x) => !isNaN(x.final) && x.final > 0)
      .sort((a, b) => b.final - a.final)
      .slice(0, 10)
      .map((item, index) => ({
        position: index + 1,
        creator: item.post.ownerName ?? 'Unknown',
        ownerAvatar: item.post.ownerAvatar ?? '',
        petTags:
          (item.post.taggedPets ?? []).map((p) => p.name).join(', ') || '',
        image: item.post.images?.[0] ?? '',
        likes: item.post.likes ?? 0,
        comments: (item.post.comments ?? []).length,
        engagementScore: isNaN(item.eng) ? 0 : Math.round(item.eng),
        finalScore: isNaN(item.final) ? 0 : Math.round(item.final),
        post: item.post,
      }));
  }, [posts, scorePost]);

  const featuredProfessionals = useMemo(() => {
    const grouped = new Map<
      string,
      {
        id: string;
        name: string;
        role: string;
        badge: string;
        verified: boolean;
        followers: string;
        image: string;
        score: number;
      }
    >();
    for (const post of posts) {
      if (!post.professionalBadge) continue;
      const key = post.ownerId || post.ownerName;
      const prev = grouped.get(key);
      const score = (post.likes ?? 0) + (post.comments ?? []).length;
      if (!prev) {
        grouped.set(key, {
          id: key,
          name: post.ownerName || 'Professional',
          role: post.professionalBadge,
          badge: '⭐',
          verified: !!post.verified,
          followers: '0',
          image: post.images?.[0] || '',
          score,
        });
      } else {
        prev.score += score;
        if (!prev.image && post.images?.[0]) {
          prev.image = post.images?.[0] ?? '';
        }
      }
    }
    return Array.from(grouped.values())
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map((pro) => ({
        ...pro,
        followers: String(pro.score),
      }));
  }, [posts]);

  const communityChallenges = useMemo(() => {
    const hashtags = new Map<string, number>();
    for (const post of posts) {
      for (const tag of extractHashtags(post.caption ?? '')) {
        hashtags.set(tag, (hashtags.get(tag) ?? 0) + 1);
      }
      for (const tag of post.tags ?? []) {
        const normalized = tag.replace(/^#/, '').toLowerCase();
        hashtags.set(normalized, (hashtags.get(normalized) ?? 0) + 1);
      }
    }
    return Array.from(hashtags.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([tag, count]) => ({
        id: tag,
        tag: `#${tag}`,
        posts: String(count),
        icon: '🔥',
      }));
  }, [posts]);

  const nearbyPosts = useMemo(
    () =>
      posts
        .filter(
          (post) =>
            post.fromPetParks ||
            Boolean(post.location) ||
            (userId && post.ownerId === userId)
        )
        .sort(
          (a, b) =>
            (b.likes ?? 0) +
            (b.comments ?? []).length * 2 -
            ((a.likes ?? 0) + (a.comments ?? []).length * 2)
        )
        .slice(0, 12),
    [posts]
  );

  const handleFollowPet = useCallback(
    async (petId: string, petName: string) => {
      setFollowLoadingPetId(petId);
      try {
        if (followedPetIds.has(petId)) {
          await socialApi.unfollow('PET', petId);
          setFollowedPetIds((prev) => {
            const next = new Set(prev);
            next.delete(petId);
            return next;
          });
          toast.success(t('social.unfollowedName', { name: petName }));
        } else {
          await socialApi.follow({ followingId: petId, followingType: 'PET' });
          setFollowedPetIds((prev) => new Set(prev).add(petId));
          toast.success(t('social.followingName', { name: petName }));
        }
      } catch {
        toast.error(t('social.failedFollowGeneric'));
      } finally {
        setFollowLoadingPetId(null);
      }
    },
    [followedPetIds, t]
  );

  const handleJoinChallenge = useCallback(
    (tagWithHash: string) => {
      const normalizedTag = tagWithHash.replace(/^#/, '').trim().toLowerCase();
      if (!normalizedTag) return;
      const challengeQuery = `#${normalizedTag}`;
      setSearchQuery(challengeQuery);
      const challengePost = posts.find((post) => {
        const captionTags = extractHashtags(post.caption ?? '');
        const postTags = (post.tags ?? []).map((tag) =>
          tag.replace(/^#/, '').toLowerCase()
        );
        return [...captionTags, ...postTags].includes(normalizedTag);
      });
      if (challengePost) setSelectedPost(challengePost);
      if (onJoinChallenge) {
        onJoinChallenge(normalizedTag);
        return;
      }
      toast.success(t('social.challengeSelected', { tag: challengeQuery }));
    },
    [posts, onJoinChallenge]
  );

  return (
    <div className="py-4 sm:py-6 px-4 sm:px-5 md:px-6 min-w-0 overflow-hidden">
      <div className="max-w-6xl mx-auto min-w-0 w-full">
        {/* Back to Social */}
        {onBackToFeed && (
          <div className="mb-5 sm:mb-6">
            <button
              type="button"
              onClick={onBackToFeed}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white/70 hover:bg-[#F0EFF5] dark:hover:bg-white/5 hover:text-[#2D2F7E] dark:hover:text-white transition-colors touch-manipulation min-h-[44px]"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <ArrowLeft className="w-4 h-4 shrink-0" />
              {t('social.backToSocial')}
            </button>
          </div>
        )}

        {/* Search Bar – prominent and touch-friendly */}
        <div className="mb-4 sm:mb-6">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#936FEF] dark:text-purple-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('social.searchPlaceholder')}
              className="w-full pl-12 pr-4 py-3.5 sm:py-3 rounded-2xl border-2 border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16] text-[#464452] dark:text-white/90 placeholder:text-[#464452]/50 dark:placeholder:text-white/50 focus:outline-none focus:border-[#936FEF] focus:ring-2 focus:ring-[#936FEF]/20 transition-all shadow-sm"
              style={{ fontFamily: 'Inter, sans-serif' }}
              aria-label={t('social.searchPlaceholder')}
            />
          </div>
          <p
            className="text-center text-xs text-[#464452] dark:text-white/50 mt-2"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('social.searchHint')}
          </p>
        </div>

        {/* Search results: pets, hashtags, posts */}
        {hasSearch && (
          <div className="mb-6 sm:mb-8 space-y-4 sm:space-y-6">
            <h2
              className="text-lg sm:text-xl text-[#2D2F7E] dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
            >
              {t('social.searchResultsFor', { query: searchQuery.trim() })}
            </h2>
            {searchResults.pets.length > 0 ||
            searchResults.hashtags.length > 0 ||
            searchResults.posts.length > 0 ? (
              <>
                {searchResults.pets.length > 0 && (
                  <div className="min-w-0">
                    <h3
                      className="text-sm font-semibold text-[#464452] dark:text-white/70 mb-2 sm:mb-3"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.pets')}
                    </h3>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {searchResults.pets.map((pet) => (
                        <button
                          key={pet.id}
                          type="button"
                          onClick={() => onViewPetProfile?.(pet.id)}
                          className="flex items-center gap-2 sm:gap-3 px-3 py-2.5 sm:px-4 sm:py-3 rounded-xl border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#16151D] hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors text-left touch-manipulation min-w-0"
                        >
                          <div className="w-12 h-12 rounded-full overflow-hidden bg-[#F0EFF5] dark:bg-white/10 flex items-center justify-center text-2xl shrink-0">
                            {pet.avatar ?? '🐾'}
                          </div>
                          <div>
                            <div
                              className="font-semibold text-[#2D2F7E] dark:text-white"
                              style={{ fontFamily: 'Sora, sans-serif' }}
                            >
                              {pet.name}
                            </div>
                            {pet.species && (
                              <div
                                className="text-xs text-[#464452] dark:text-white/60"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                {t(`petOnboarding.step1.${pet.species}`, pet.species)}
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {searchResults.hashtags.length > 0 && (
                  <div className="min-w-0">
                    <h3
                      className="text-sm font-semibold text-[#464452] dark:text-white/70 mb-2 sm:mb-3"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.hashtags')}
                    </h3>
                    <div className="flex flex-wrap gap-2 sm:gap-2.5">
                      {searchResults.hashtags.map(({ tag, count }) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-full bg-[#F0EFF5] dark:bg-white/10 text-[#2D2F7E] dark:text-white font-medium text-sm"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          <Hash className="w-4 h-4 text-[#936FEF]" />
                          {tag}
                          <span className="text-xs text-[#464452] dark:text-white/60">
                            {t(
                              count === 1
                                ? 'social.postsCountSingle'
                                : 'social.postsCountPlural',
                              { count }
                            )}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {searchResults.posts.length > 0 && (
                  <div className="min-w-0">
                    <h3
                      className="text-sm font-semibold text-[#464452] dark:text-white/70 mb-2 sm:mb-3"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.posts')}
                    </h3>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      {searchResults.posts.map((post) => (
                        <div
                          key={post.id}
                          className="aspect-square rounded-xl sm:rounded-lg overflow-hidden bg-[#F0EFF5] dark:bg-white/10"
                        >
                          {post.images?.[0] ? (
                            <SocialPostImage
                              src={post.images[0]}
                              alt={post.ownerName ?? t('social.post')}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#464452] dark:text-white/40 text-2xl">
                              📷
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white dark:bg-[#16151D] rounded-2xl p-5 sm:p-6 md:p-8 border border-[#E2DFEA] dark:border-white/8 text-center shadow-sm">
                <Search className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 text-[#464452] dark:text-white/40" />
                <p
                  className="text-[#464452] dark:text-white/70 text-sm sm:text-base"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('social.noSearchResultsFor', {
                    query: searchQuery.trim(),
                  })}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Category Tabs – touch-friendly, consistent with feed filters */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-2.5 mb-5 sm:mb-6">
          {[
            {
              id: 'trending',
              labelKey: 'social.tabTrending',
              icon: TrendingUp,
            },
            { id: 'nearby', labelKey: 'social.tabNearby', icon: MapPin },
            {
              id: 'professionals',
              labelKey: 'social.tabProfessionals',
              icon: Award,
            },
            { id: 'challenges', labelKey: 'social.tabChallenges', icon: Hash },
          ].map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => {
                  const id = category.id as ExploreCategory;
                  setActiveCategory(id);
                  if (onFilterClick) {
                    onFilterClick(id);
                  }
                }}
                className={`flex items-center gap-2 px-4 py-2.5 sm:py-2 rounded-full whitespace-nowrap transition-all touch-manipulation min-h-[44px] sm:min-h-0 ${
                  activeCategory === category.id
                    ? 'text-white'
                    : 'bg-[#F0EFF5] dark:bg-white/10 text-[#464452] dark:text-white/70 hover:bg-[#E2DFEA] dark:hover:bg-white/15'
                }`}
                style={
                  activeCategory === category.id
                    ? {
                        background:
                          'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                        fontFamily: 'Inter, sans-serif',
                      }
                    : { fontFamily: 'Inter, sans-serif' }
                }
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{t(category.labelKey)}</span>
              </button>
            );
          })}
        </div>

        {/* Trending Pets */}
        {activeCategory === 'trending' && selectedPetFeed && (
          <TrendingPetFeedView
            pet={selectedPetFeed}
            posts={posts}
            userId={userId}
            onBack={() => setSelectedPetFeed(null)}
            followedPetIds={followedPetIds}
            followLoadingPetId={followLoadingPetId}
            onFollowPet={handleFollowPet}
            onViewPetProfile={onViewPetProfile}
          />
        )}

        {activeCategory === 'trending' && !selectedPetFeed && (
          <div className="min-w-0">
            <h2
              className="text-lg sm:text-xl mb-3 sm:mb-4 text-[#2D2F7E] dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
            >
              {t('social.trendingPets')}
            </h2>
            {trendingPets.length === 0 ? (
              <div className="bg-white dark:bg-[#16151D] rounded-2xl p-6 sm:p-8 md:p-12 border border-[#E2DFEA] dark:border-white/8 text-center mb-6 sm:mb-8 shadow-sm">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#F0EFF5] dark:bg-white/10 text-[#936FEF] mb-3 text-2xl">
                  🐾
                </div>
                <p
                  className="text-[#464452] dark:text-white/70 text-sm sm:text-base"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('social.noTrendingPetsYet')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
                {trendingPets.map((pet) => (
                  <div
                    key={pet.id}
                    className="bg-white dark:bg-[#16151D] rounded-2xl p-3 sm:p-4 border border-[#E2DFEA] dark:border-white/8 hover:shadow-lg dark:hover:shadow-purple-900/20 transition-shadow min-w-0"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (pet.isTagged && onViewPetProfile) {
                          onViewPetProfile(pet.id, pet.name, pet.avatar, Number(pet.followers) || 0);
                        } else if (pet.topPost) {
                          setSelectedPost(pet.topPost);
                        }
                      }}
                      className="aspect-square mb-2 sm:mb-3 rounded-xl overflow-hidden cursor-pointer bg-[#F0EFF5] dark:bg-white/10 w-full block"
                    >
                      {pet.image ? (
                        <SocialPostImage
                          src={pet.image}
                          alt={pet.name}
                          className="w-full h-full object-cover"
                        />
                      ) : pet.avatar?.startsWith('http') ? (
                        <img
                          src={pet.avatar}
                          alt={pet.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl">
                          {pet.avatar || '🐾'}
                        </div>
                      )}
                    </button>
                    <div className="flex items-center gap-2 mb-2 min-w-0">
                      {pet.avatar?.startsWith('http') ? (
                        <img
                          src={pet.avatar}
                          alt={pet.name}
                          className="w-8 h-8 rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <span className="text-xl sm:text-2xl shrink-0">
                          {pet.avatar || '🐾'}
                        </span>
                      )}
                      <div>
                        <div
                          className="font-semibold text-[#2D2F7E] dark:text-white"
                          style={{ fontFamily: 'Sora, sans-serif' }}
                        >
                          {pet.name}
                        </div>
                        <div
                          className="text-xs text-[#464452] dark:text-white/70"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {pet.followers} Trending Score
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleFollowPet(pet.id, pet.name)}
                      disabled={followLoadingPetId === pet.id}
                      className="w-full py-2 rounded-xl text-white text-sm hover:opacity-90 transition-opacity"
                      style={{
                        background: followedPetIds.has(pet.id)
                          ? '#E2DFEA'
                          : 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                        color: followedPetIds.has(pet.id) ? '#464452' : '#fff',
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {followLoadingPetId === pet.id
                        ? t('social.loadingFeed')
                        : followedPetIds.has(pet.id)
                          ? t('social.following')
                          : t('social.profile.follow')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPetFeed({
                          id: pet.id,
                          name: pet.name,
                          avatar: pet.avatar,
                          image: pet.image,
                          ownerName: pet.topPost?.ownerName,
                          ownerAvatar: pet.topPost?.ownerAvatar,
                        });
                      }}
                      className="w-full mt-1.5 py-1.5 rounded-xl text-xs font-medium text-[#936FEF] border border-[#936FEF]/30 hover:bg-[#936FEF]/10 transition-colors"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      View posts →
                    </button>
                  </div>
                ))}
              </div>
            )}

            <h2
              className="text-lg sm:text-xl mb-3 sm:mb-4 text-[#2D2F7E] dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
            >
              {t('social.explorePosts')}
            </h2>
            {exploreGrid.length === 0 ? (
              <div className="bg-white dark:bg-[#16151D] rounded-2xl p-6 sm:p-8 md:p-12 border border-[#E2DFEA] dark:border-white/8 text-center shadow-sm">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#F0EFF5] dark:bg-white/10 text-[#936FEF] mb-3 text-2xl">
                  📷
                </div>
                <p
                  className="text-[#464452] dark:text-white/70 text-sm sm:text-base"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('social.noPostsToExploreYet')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {exploreGrid.map((post) => (
                  <button
                    key={post.id}
                    type="button"
                    onClick={() => {
                      const selected = posts.find((p) => p.id === post.id);
                      if (selected) setSelectedPost(selected);
                    }}
                    className="aspect-square relative group overflow-hidden rounded-xl sm:rounded-lg touch-manipulation"
                  >
                    <SocialPostImage
                      src={post.image}
                      alt="Post"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="text-white flex items-center gap-2">
                        <span className="text-2xl">❤️</span>
                        <span>{post.likes}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Trending Leaderboard */}
            {trendingLeaderboard.length > 0 && (
              <div className="mt-6 sm:mt-8">
                <h2
                  className="text-lg sm:text-xl mb-3 sm:mb-4 text-[#2D2F7E] dark:text-white flex items-center gap-2"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  <TrendingUp className="w-5 h-5 text-[#936FEF]" />
                  Trending Rankings
                </h2>
                <div className="space-y-2">
                  {trendingLeaderboard.map((row) => {
                    const maxScore = trendingLeaderboard[0]?.finalScore || 1;
                    const barPct = Math.round(
                      (row.finalScore / maxScore) * 100
                    );
                    const medals: Record<number, string> = {
                      1: '🥇',
                      2: '🥈',
                      3: '🥉',
                    };
                    const topGradients: Record<number, string> = {
                      1: 'from-amber-50 to-yellow-50/30 dark:from-amber-900/20 dark:to-yellow-900/10 border-amber-200/60 dark:border-amber-500/20',
                      2: 'from-slate-50 to-gray-50/30 dark:from-slate-700/20 dark:to-gray-700/10 border-slate-200/60 dark:border-slate-500/20',
                      3: 'from-orange-50 to-amber-50/30 dark:from-orange-900/20 dark:to-amber-900/10 border-orange-200/60 dark:border-orange-500/20',
                    };
                    const isTop3 = row.position <= 3;
                    return (
                      <button
                        key={row.position}
                        type="button"
                        onClick={() => setSelectedPost(row.post)}
                        className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-2xl border transition-all hover:shadow-md active:scale-[0.99] ${
                          isTop3
                            ? `bg-gradient-to-r ${topGradients[row.position]}`
                            : 'bg-white dark:bg-[#16151D] border-[#E2DFEA] dark:border-white/8 hover:bg-[#F8F7FC] dark:hover:bg-white/5'
                        }`}
                      >
                        {/* Rank badge */}
                        <div className="w-8 shrink-0 flex items-center justify-center">
                          {isTop3 ? (
                            <span className="text-xl leading-none">
                              {medals[row.position]}
                            </span>
                          ) : (
                            <span
                              className="w-6 h-6 rounded-full bg-[#F0EFF5] dark:bg-white/10 flex items-center justify-center text-[11px] font-bold text-[#464452] dark:text-white/50"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              {row.position}
                            </span>
                          )}
                        </div>

                        {/* Post thumbnail */}
                        <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0 bg-[#F0EFF5] dark:bg-white/10">
                          {row.image ? (
                            <SocialPostImage
                              src={row.image}
                              alt={row.creator}
                              className="w-full h-full object-cover"
                            />
                          ) : row.ownerAvatar?.startsWith('http') ? (
                            <img
                              src={row.ownerAvatar}
                              alt={row.creator}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-lg">
                              🐾
                            </div>
                          )}
                        </div>

                        {/* Info + bar */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-1.5 min-w-0">
                            <span
                              className="text-sm font-semibold text-[#2D2F7E] dark:text-white truncate"
                              style={{ fontFamily: 'Sora, sans-serif' }}
                            >
                              {row.creator}
                            </span>
                            {row.petTags && (
                              <span
                                className="text-[11px] text-[#936FEF] truncate shrink-0"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                🐾 {row.petTags}
                              </span>
                            )}
                          </div>
                          {/* Engagement metrics */}
                          <div
                            className="flex items-center gap-2 mt-0.5 mb-1.5"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            <span className="text-[10px] text-[#464452] dark:text-white/40">
                              ❤️ {row.likes}
                            </span>
                            <span className="text-[10px] text-[#464452] dark:text-white/40">
                              💬 {row.comments}
                            </span>
                          </div>
                          {/* Score bar */}
                          <div className="h-1 rounded-full bg-[#E2DFEA] dark:bg-white/10 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-[#936FEF] to-[#7B5CE5] transition-all duration-500"
                              style={{ width: `${barPct}%` }}
                            />
                          </div>
                        </div>

                        {/* Score */}
                        <div className="text-right shrink-0 pl-1">
                          <div
                            className="text-sm font-bold text-[#936FEF]"
                            style={{ fontFamily: 'Sora, sans-serif' }}
                          >
                            {row.finalScore.toLocaleString()}
                          </div>
                          <div
                            className="text-[9px] text-[#464452] dark:text-white/40 uppercase tracking-wide"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            score
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p
                  className="text-[10px] text-[#464452] dark:text-white/40 mt-3 text-center"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Ranked by engagement quality · velocity · recency ·
                  credibility
                </p>
              </div>
            )}
          </div>
        )}

        {/* Featured Professionals */}
        {activeCategory === 'professionals' && (
          <div className="min-w-0">
            <h2
              className="text-lg sm:text-xl mb-3 sm:mb-4 text-[#2D2F7E] dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
            >
              {t('social.featuredProfessionals')}
            </h2>
            {featuredProfessionals.length === 0 ? (
              <div className="bg-white dark:bg-[#16151D] rounded-2xl p-6 sm:p-8 md:p-12 border border-[#E2DFEA] dark:border-white/8 text-center shadow-sm">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#F0EFF5] dark:bg-white/10 text-[#936FEF] mb-3 text-2xl">
                  ⭐
                </div>
                <p
                  className="text-[#464452] dark:text-white/70 text-sm sm:text-base"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('social.noProfessionalsYet')}
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                {featuredProfessionals.map((pro) => (
                  <div
                    key={pro.id}
                    className="bg-white dark:bg-[#16151D] rounded-2xl p-4 sm:p-6 border border-[#E2DFEA] dark:border-white/8 hover:shadow-lg dark:hover:shadow-purple-900/20 transition-shadow min-w-0"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4 min-w-0">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shrink-0 bg-[#F0EFF5] dark:bg-white/10">
                        {pro.image ? (
                          <SocialPostImage
                            src={pro.image}
                            alt={pro.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-2xl">
                            👤
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3
                            className="text-[#2D2F7E] dark:text-white"
                            style={{
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                            }}
                          >
                            {pro.name}
                          </h3>
                          {pro.verified && (
                            <span className="text-[#936FEF]">✓</span>
                          )}
                        </div>
                        <div
                          className="flex items-center gap-2 text-sm text-[#464452] dark:text-white/70"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          <span>{pro.badge}</span>
                          <span>{pro.role}</span>
                        </div>
                        <div
                          className="text-xs mt-1 text-[#464452] dark:text-white/50 opacity-60"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {pro.followers} {t('social.followers')}
                        </div>
                      </div>
                    </div>
                    <button
                      className="w-full py-2 rounded-xl text-white hover:opacity-90 transition-opacity"
                      style={{
                        background:
                          'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {t('social.profile.follow')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Community Challenges */}
        {activeCategory === 'challenges' && (
          <div className="min-w-0">
            <h2
              className="text-lg sm:text-xl mb-3 sm:mb-4 text-[#2D2F7E] dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
            >
              {t('social.communityChallenges')}
            </h2>
            {communityChallenges.length === 0 ? (
              <div className="bg-white dark:bg-[#16151D] rounded-2xl p-6 sm:p-8 md:p-12 border border-[#E2DFEA] dark:border-white/8 text-center mb-6 sm:mb-8 shadow-sm">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#F0EFF5] dark:bg-white/10 text-[#936FEF] mb-3 text-2xl">
                  #
                </div>
                <p
                  className="text-[#464452] dark:text-white/70 text-sm sm:text-base"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('social.noChallengesYet')}
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                {communityChallenges.map((challenge) => (
                  <div
                    key={challenge.id}
                    className="bg-white dark:bg-[#16151D] rounded-2xl p-4 sm:p-6 border border-[#E2DFEA] dark:border-white/8 hover:shadow-lg dark:hover:shadow-purple-900/20 transition-shadow min-w-0"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4 min-w-0">
                      <div
                        className="w-16 h-16 rounded-xl flex items-center justify-center text-3xl"
                        style={{
                          background:
                            'linear-gradient(135deg, #F0EFF5 0%, #936FEF 100%)',
                        }}
                      >
                        {challenge.icon}
                      </div>
                      <div className="flex-1">
                        <h3
                          className="text-lg mb-1 text-[#2D2F7E] dark:text-white"
                          style={{
                            fontFamily: 'Sora, sans-serif',
                            fontWeight: 700,
                          }}
                        >
                          {challenge.tag}
                        </h3>
                        <div
                          className="text-sm text-[#464452] dark:text-white/70"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {t('social.postsCountPlural', {
                            count: challenge.posts,
                          })}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleJoinChallenge(challenge.tag)}
                      className="w-full py-2 rounded-xl text-white hover:opacity-90 transition-opacity"
                      style={{
                        background:
                          'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {t('social.joinChallenge')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 sm:mt-8 rounded-2xl p-4 sm:p-6 bg-gradient-to-r from-purple-50 to-purple-100/10 dark:from-purple-900/20 dark:to-purple-800/10 border border-[#E2DFEA] dark:border-white/10">
              <h3
                className="mb-2 text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('social.howChallengesWorkTitle')}
              </h3>
              <ul
                className="space-y-2 text-sm text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <li>• {t('social.howChallengesWorkItem1')}</li>
                <li>• {t('social.howChallengesWorkItem2')}</li>
                <li>• {t('social.howChallengesWorkItem3')}</li>
                <li>• {t('social.howChallengesWorkItem4')}</li>
              </ul>
            </div>
          </div>
        )}

        {/* Nearby Pets */}
        {activeCategory === 'nearby' && (
          <div className="min-w-0">
            <h2
              className="text-lg sm:text-xl mb-3 sm:mb-4 text-[#2D2F7E] dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
            >
              {t('social.tabNearby')}
            </h2>
            {nearbyPosts.length === 0 ? (
              <div className="bg-white dark:bg-[#16151D] rounded-2xl p-6 sm:p-8 md:p-12 border border-[#E2DFEA] dark:border-white/8 text-center shadow-sm">
                <MapPin className="w-16 h-16 mx-auto mb-4 text-[#464452] dark:text-white/40 opacity-40" />
                <p
                  className="text-[#464452] dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('social.noPostsToExploreYet')}
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                {nearbyPosts.map((post) => (
                  <div
                    key={post.id}
                    className="bg-white dark:bg-[#16151D] rounded-2xl p-4 border border-[#E2DFEA] dark:border-white/8"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div
                        className="font-semibold text-[#2D2F7E] dark:text-white"
                        style={{ fontFamily: 'Sora, sans-serif' }}
                      >
                        {post.ownerName}
                      </div>
                      <div
                        className="text-xs text-[#464452] dark:text-white/60"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {post.location || t('social.petParks')}
                      </div>
                    </div>
                    {post.images?.[0] && (
                      <div className="aspect-video rounded-xl overflow-hidden mb-3 bg-[#F0EFF5] dark:bg-white/10">
                        <SocialPostImage
                          src={post.images[0]}
                          alt={post.ownerName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <p
                      className="text-sm text-[#464452] dark:text-white/70 line-clamp-2"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {post.caption}
                    </p>
                    <div
                      className="mt-3 text-xs text-[#464452] dark:text-white/60"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.likesComments', {
                        likes: post.likes ?? 0,
                        comments: (post.comments ?? []).length,
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {selectedPost && (
        <PostDetailModal
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onLike={(postId) => {
            const post = posts.find((p) => p.id === postId);
            if (!post) return;
            if (post.isLiked) unlikePost(postId);
            else likePost(postId);
          }}
          onAddComment={(postId, text) => {
            addComment(postId, text);
          }}
        />
      )}
    </div>
  );
}
